/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { Editor, EditorProvider } from 'react-simple-wysiwyg';
import ReactMarkdown from 'react-markdown';
import { PromptCreationPractice, submitPracticeProgress } from '../../../../api/lessonsService';
import './PromptCreationDialog.css';
import {useLessonStore} from "../../../../store/useLessonStore";

interface PromptCreationDialogProps {
  practice: PromptCreationPractice;
  lessonId: number;
  fragmentIndex: number;
  practiceId: number;
  practiceType: string;
  isOpen: boolean;
  courseId: string;
  currentFragmentId:number;
  onClose: () => void;
  onResetResult: () => void;
  onSubmit: (prompt: string) => void;
  evaluationResult?: any;
  isEvaluating?: boolean;
  onCompleted?: () => void;
  onMoveText?: () => void;
}

export const PromptCreationDialog: React.FC<PromptCreationDialogProps> = ({
  courseId,
  currentFragmentId,
  practice,
  lessonId,
  fragmentIndex,
  practiceId,
  practiceType,
  isOpen,
  onClose,
  onSubmit,
  onResetResult,
  evaluationResult,
  isEvaluating,
  onCompleted,
  onMoveText
}) => {
  const { setLessonProgressData } = useLessonStore()
  const [prompt, setPrompt] = useState('');
  const [showHint, setShowHint] = useState(false);
  const inputContainerRef = useRef<HTMLDivElement | null>(null);

  // Scroll input container into view when user focuses/clicks the textarea
  const scrollInputIntoView = () => {
    if (inputContainerRef.current) {
      try {
        inputContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      } catch (e) {
        // Fallback for older browsers
        const el = inputContainerRef.current as HTMLElement;
        const top = el.getBoundingClientRect().top + window.pageYOffset - 80; // slight offset for header
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }
  };

  // Auto-scroll the input container into view when evaluation feedback appears
  useEffect(() => {
    if (evaluationResult && inputContainerRef.current) {
      try {
        inputContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (e) {
        // Fallback for older browsers
        const el = inputContainerRef.current as HTMLElement;
        const top = el.getBoundingClientRect().top + window.pageYOffset - 80; // slight offset for header
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }
  }, [evaluationResult]);

  const stripHtml = (html: string) => html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');

  const handleSubmit = () => {
    if (stripHtml(prompt).trim()) {
      onSubmit(prompt);
    }
  };

  const handleContinue = async () => {
    // Submit practice progress when user continues
    if (evaluationResult && stripHtml(prompt).trim()) {
      try {
        const isCorrect = parseInt(evaluationResult.score) >= 3;
        const result = await submitPracticeProgress({
          fragmentId: currentFragmentId,
          courseId: courseId,
          lessonId: lessonId,
          fragmentIndex: fragmentIndex,
          practiceId: practiceId,
          practiceType: practiceType,
          answer: prompt,
          isCorrect:isCorrect
        });
       setLessonProgressData([result])
      } catch (error) {
        console.error('Error submitting practice progress:', error);
      }
    }

    setPrompt('');
    onResetResult();
    onClose();
    onCompleted?.();
    onMoveText?.();
  };

  const handleClose = () => {
    setPrompt('');
    setShowHint(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="prompt-dialog-overlay">
      <div className="prompt-dialog">
        {/* Header */}
        <div className="prompt-dialog-header">
          <button className="prompt-dialog-close" onClick={handleClose}>
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="prompt-dialog-content">
            <h1 className="prompt-dialog-title">{practice.title}</h1>
          <div className="prompt-dialog-instructions">
            <div className="prompt-dialog-text">
              <ReactMarkdown
                components={{
                  img: ({ node, ...props }) => (
                    <img
                      {...props}
                      alt={props.alt || 'description-image'}
                      style={{
                        maxWidth: '100%',
                        height: 'auto',
                        margin: '16px 0',
                        borderRadius: '12px'
                      }}
                    />
                  ),
                  h1: ({ node, ...props }) => (
                    <h1 {...props} style={{ fontSize: '1.6rem', fontWeight: 700, margin: '16px 0' }} />
                  ),
                  h2: ({ node, ...props }) => (
                    <h2 {...props} style={{ fontSize: '1.4rem', fontWeight: 600, margin: '14px 0' }} />
                  ),
                  h3: ({ node, ...props }) => (
                    <h3 {...props} style={{ fontSize: '1.2rem', fontWeight: 600, margin: '12px 0' }} />
                  ),
                  p: ({ node, ...props }) => (
                    <p {...props} style={{ marginBottom: '12px', lineHeight: '1.7' }} />
                  ),
                  ul: ({ node, ...props }) => (
                    <ul {...props} style={{ margin: '12px 0', paddingLeft: '22px' }} />
                  ),
                  ol: ({ node, ...props }) => (
                    <ol {...props} style={{ margin: '12px 0', paddingLeft: '22px' }} />
                  ),
                  li: ({ node, ...props }) => (
                    <li {...props} style={{ marginBottom: '6px' }} />
                  ),
                  blockquote: ({ node, ...props }) => (
                    <blockquote
                      {...props}
                      style={{
                        borderLeft: '4px solid #3b6cff',
                        paddingLeft: '12px',
                        margin: '12px 0',
                        fontStyle: 'italic',
                        color: '#555'
                      }}
                    />
                  ),
                  code: ({ node, ...props }: any) => {
                    const isInline = !node?.children?.[0]?.type;
                    if (isInline) {
                      return (
                        <code
                          {...props}
                          style={{
                            background: '#f1f3f4',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
                            fontSize: '0.9em'
                          }}
                        />
                      );
                    }
                    return (
                      <pre
                        style={{
                          background: '#f8f9fa',
                          padding: '12px',
                          borderRadius: '8px',
                          overflowX: 'auto',
                          margin: '12px 0'
                        }}
                      >
                        <code {...props} style={{ background: 'none', padding: 0 }} />
                      </pre>
                    );
                  }
                }}
              >
                {practice.fullDescription}
              </ReactMarkdown>
            </div>
          </div>

          {/* ChatGPT Input Box */}
          <div className="prompt-dialog-input-container" ref={inputContainerRef}>
            <div className="prompt-dialog-input-header">
              <div className="prompt-dialog-chatgpt-icon">
                <img
                  src="/images/ui/practice-logo.png"
                  alt="Practice Logo"
                  width="20"
                  height="20"
                />
              </div>
              <span className="prompt-dialog-chatgpt-text">Practice</span>
            </div>
            <div className="prompt-dialog-editor-wrapper" onClick={scrollInputIntoView}>
              <EditorProvider>
                {/*<Toolbar>*/}
                {/*  <BtnUndo />*/}
                {/*  <BtnRedo />*/}
                {/*  <Separator />*/}
                {/*  <BtnBold />*/}
                {/*  <BtnItalic />*/}
                {/*  <BtnUnderline />*/}
                {/*  <BtnStrikeThrough />*/}
                {/*  <Separator />*/}
                {/*  <BtnNumberedList />*/}
                {/*  <BtnBulletList />*/}
                {/*</Toolbar>*/}
                <Editor
                  className="prompt-dialog-textarea"
                  value={evaluationResult && parseInt(evaluationResult.score) <= 3
                    ? `<p><strong>Original:</strong> ${prompt}</p><p><strong>Suggestion:</strong> ${evaluationResult.recommendedPrompt}</p>`
                    : prompt
                  }
                  onChange={(e) => setPrompt((e.target as HTMLTextAreaElement).value)}
                  onFocus={scrollInputIntoView}
                  containerProps={{ style: { minHeight: 120, background: '#F3F5F9' } }}
                  disabled={Boolean(evaluationResult && parseInt(evaluationResult.score) < 3)}
                />
              </EditorProvider>
            </div>
          </div>

          {showHint && (
            <div className="prompt-dialog-hint-section">
              <div className="prompt-dialog-hint-content">
                <p>{practice.tip}</p>
              </div>
            </div>
          )}
        </div>

          {isEvaluating ? (
            <div className="evaluation-loading">
              <p>Evaluating your prompt...</p>
            </div>
          ) : evaluationResult ? (
            <div className={`evaluation-feedback ${parseInt(evaluationResult.score) <= 3 ? 'error' : 'success'}`}>
              <div className="evaluation-feedback-icon">
                {parseInt(evaluationResult.score) <= 3 ? (
                  <div className="evaluation-icon error">!</div>
                ) : (
                  <div className="evaluation-icon success">✓</div>
                )}
                <h3 className="evaluation-feedback-title">
                  {parseInt(evaluationResult.score) <= 3 ? 'Oops!' : 'Amazing!'}
                </h3>
              </div>
              <div className="evaluation-feedback-content">
                <p className="evaluation-feedback-message">{evaluationResult.comment}</p>
                <button
                  className="evaluation-feedback-button"
                  onClick={handleContinue}
                >
                  {parseInt(evaluationResult.score) <= 3 ? 'CONTINUE' : 'CONTINUE'}
                </button>
              </div>
            </div>
          ) : (
            <div className="prompt-dialog-footer">
              <button
                className="prompt-dialog-hint-btn"
                onClick={() => setShowHint(!showHint)}
              >
                Hint
              </button>
              <button
                className="prompt-dialog-submit-btn"
                onClick={handleSubmit}
                disabled={!stripHtml(prompt).trim()}
              >
               Enter your prompt...
              </button>
            </div>
          )}
      </div>
    </div>
  );
};
